/* Базовые кирпичики интерфейса. Оформление подогнано под Konsta iOS,
   цвета берутся из темы Telegram (ТЗ 11). */

import { Block, ListInput } from "konsta/react";
import React from "react";
import { health as healthOf, rub, TIER_META } from "@mera/shared";
import Logo from "../assets/logo.svg?react";
import { Avatar } from "./AvatarPicker.jsx";
import { Ion } from "./icons.jsx";
import { openLink } from "../lib/tg.js";

/* ── Текст ── */
export const Eyebrow = ({ children, className = "" }) => (
  <div className={`eyebrow px-4 pb-1.5 pt-4 ${className}`}>{children}</div>
);

export const Hint = ({ children, className = "" }) => (
  <span className={`text-[13px] ${className}`} style={{ color: "var(--tg-hint)" }}>
    {children}
  </span>
);

/* ── Деньги. Семантика: доход зелёный, расход красный, ожидание синее (ТЗ 11) ── */
export function Money({ value, sign, className = "", strong }) {
  const v = Number(value) || 0;
  const color =
    sign === "in" || (sign === "auto" && v > 0)
      ? "var(--fin-green)"
      : sign === "out" || (sign === "auto" && v < 0)
        ? "var(--fin-red)"
        : sign === "wait"
          ? "var(--fin-blue)"
          : "var(--tg-text)";
  return (
    <span
      className={`num ${strong ? "font-bold" : "font-semibold"} ${className}`}
      style={{ color }}
    >
      {rub(v)}
    </span>
  );
}

/* ── Карточка-плитка метрики ──
   Плитка одна на всё приложение: сводка финансов, сделка, проект и воронки CRM
   выглядят одинаково — плотные поля, мелкая надпись, скругление меньше карточного.
   Суммы пишем полностью, поэтому число бывает длинным: кегль подбирается под длину,
   чтобы «1 250 000 ₽» не переносилось и не обрезалось даже в ряду из трёх. */
const fitSize = (v) => {
  const len = String(v ?? "").length;
  if (len > 12) return 11;
  if (len > 9) return 12;
  if (len > 7) return 13;
  return 15;
};

export function Metric({ label, value, sub, color, onClick, className = "" }) {
  return (
    <div
      className={`card-tg min-w-0 px-2.5 py-2 ${onClick ? "press" : ""} ${className}`}
      /* 24px скругления плитке великоваты: угол съедает её и без того небольшую
         высоту. Радиус меньше — карточка читается как плитка, а не как капля. */
      style={{ borderRadius: 14 }}
      onClick={onClick}
      role={onClick ? "button" : undefined}
    >
      <div
        className="eyebrow px-0 pt-0 pb-1 leading-tight !text-[10px] !tracking-[0.04em]"
        style={{ overflowWrap: "anywhere" }}
      >
        {label}
      </div>
      <div
        className="num font-bold leading-tight whitespace-nowrap overflow-hidden"
        /* pl компенсирует отрицательный трекинг Space Grotesk — иначе overflow срезает первый глиф */
        style={{ color: color || "var(--tg-text)", fontSize: fitSize(value), paddingLeft: 2, marginLeft: -2 }}
      >
        {value}
      </div>
      {sub ? (
        <div className="mt-0.5 truncate text-[10px]" style={{ color: "var(--tg-hint)" }}>
          {sub}
        </div>
      ) : null}
    </div>
  );
}

export const MetricGrid = ({ children, cols = 2, className = "" }) => (
  <div className={`grid gap-2 px-4 ${cols === 3 ? "grid-cols-3" : "grid-cols-2"} ${className}`}>
    {children}
  </div>
);

/* ── Текст, написанный человеком ──
   Заметки пишут как в мессенджере: с переносами строк и ссылками. Поэтому здесь
   переносы сохраняются, длинная ссылка ломается по любому символу (иначе она
   распирала бы карточку), а сам текст можно выделить и скопировать — в вебвью
   Telegram выделение приходится разрешать явно. Ссылку открываем через Telegram:
   обычный переход из мини-аппа он не пускает. */
const URL_RE = /(https?:\/\/[^\s]+|www\.[^\s]+)/gi;
const IS_URL = /^(https?:\/\/|www\.)/i;

export function RichText({ children, className = "" }) {
  const text = String(children ?? "");
  const parts = text.split(URL_RE);

  return (
    <span
      data-nodrag
      className={`whitespace-pre-wrap [overflow-wrap:anywhere] ${className}`}
      style={{ WebkitUserSelect: "text", userSelect: "text", WebkitTouchCallout: "default" }}
    >
      {parts.map((part, i) =>
        IS_URL.test(part) ? (
          <a
            key={i}
            href={part}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              openLink(part.startsWith("www.") ? `https://${part}` : part);
            }}
            style={{ color: "var(--tg-link)" }}
          >
            {part}
          </a>
        ) : (
          <React.Fragment key={i}>{part}</React.Fragment>
        )
      )}
    </span>
  );
}

/* ── Burn и запас хода парой карточек ──
   Это не часть баланса, а вывод из него, поэтому карточки отдельные. Рисуют их
   и обзор, и список регулярных расходов — цифра там одна и та же, и выглядеть
   она должна одинаково. `onBurnClick` есть только там, откуда ведут в расходы. */
export function BurnCards({ total, burn, onBurnClick }) {
  const runway = burn ? +(total / burn).toFixed(1) : null;

  return (
    <div className="px-4 pt-2 grid grid-cols-2 gap-2">
      <div
        className={`card-tg px-4 py-3 relative overflow-hidden ${onBurnClick ? "press" : ""}`}
        onClick={onBurnClick}
        role={onBurnClick ? "button" : undefined}
      >
        {onBurnClick ? (
          <span
            className="absolute right-2.5 top-2.5 w-7 h-7 rounded-full flex items-center justify-center"
            style={{
              background: "color-mix(in srgb, var(--tg-hint) 16%, transparent)",
              color: "var(--tg-hint)",
            }}
          >
            <Ion.chevronForward size={15} />
          </span>
        ) : null}
        <div className="eyebrow px-0 pt-0 pb-1">Burn</div>
        <div className="num text-[19px] font-bold">{rub(burn)}</div>
      </div>

      <div className="card-tg px-4 py-3 overflow-hidden">
        <div className="eyebrow px-0 pt-0 pb-1">Runway</div>
        <div className="num text-[19px] font-bold">{runway != null ? `${runway} мес` : "—"}</div>
      </div>
    </div>
  );
}

/* ── Индикатор финздоровья: красный → жёлтый → зелёный (ТЗ 7.2) ──
   С `accent` шкала рисуется акцентным синим: там, где полоса показывает не
   тревогу, а ход к цели, красный читался бы как «всё плохо». */
export function HealthBar({ t, label, value, caption, accent = false }) {
  const h = healthOf(t);
  const color = accent ? "var(--tg-button)" : h.color;
  const track = accent ? "color-mix(in srgb, var(--tg-button) 16%, transparent)" : h.bg;
  const border = accent ? "color-mix(in srgb, var(--tg-button) 24%, transparent)" : h.border;
  return (
    <div className="card-tg px-4 py-3.5" style={{ boxShadow: `inset 0 0 0 1px ${border}` }}>
      <div className="flex items-baseline justify-between gap-3">
        <div className="eyebrow px-0 pt-0 pb-0">{label}</div>
        <div className="num text-[17px] font-bold" style={{ color }}>
          {value}
        </div>
      </div>
      <div className="mt-2 h-1.5 rounded-full overflow-hidden" style={{ background: track }}>
        <div
          className="h-full rounded-full transition-[width] duration-500"
          style={{ width: `${Math.round(Math.max(0, Math.min(1, t)) * 100)}%`, background: color }}
        />
      </div>
      {caption ? (
        <div className="mt-1.5 text-[12px]" style={{ color: "var(--tg-hint)" }}>
          {caption}
        </div>
      ) : null}
    </div>
  );
}

export function HealthDot({ t, size = 8 }) {
  const h = healthOf(t);
  return (
    <span
      className="inline-block rounded-full shrink-0"
      style={{ width: size, height: size, background: h.color }}
    />
  );
}

/* ── Тир клиента (ТЗ 5.6) ── */
export function TierBadge({ tier, size = 20 }) {
  const m = TIER_META[tier];
  if (!m) return null;
  return (
    <span
      className="num inline-flex items-center justify-center rounded-md font-bold shrink-0"
      style={{
        width: size,
        height: size,
        fontSize: size * 0.55,
        color: m.color,
        background: m.bg,
        boxShadow: `inset 0 0 0 1px ${m.color}40`,
      }}
    >
      {m.label}
    </span>
  );
}

/* ── Пилюля-статус ── */
export function Pill({ children, color, bg, className = "" }) {
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full px-2 py-[3px] text-[11px] font-semibold ${className}`}
      style={{
        color: color || "var(--tg-hint)",
        background: bg || "color-mix(in srgb, var(--tg-hint) 12%, transparent)",
      }}
    >
      {children}
    </span>
  );
}

/* ── Полоса прогресса этапа: серый → акцент (ТЗ 6.3, 6.4) ── */
export const StageBar = ({ color }) => (
  <div className="h-[3px] rounded-full mt-1.5" style={{ background: color, opacity: 0.85 }} />
);

/* ── Бар для разбивки категорий (ТЗ 6.2) ── */
export function Bar({ value, color, height = 6 }) {
  return (
    <div
      className="w-full rounded-full overflow-hidden"
      style={{ height, background: "color-mix(in srgb, var(--tg-hint) 14%, transparent)" }}
    >
      <div
        className="h-full rounded-full transition-[width] duration-500"
        style={{ width: `${Math.max(0, Math.min(100, value))}%`, background: color || "var(--tg-button)" }}
      />
    </div>
  );
}

/* ── Состояния ──
   Ожидание в интерфейсе показывается только логотипом: он дышит цветом подсказок,
   пока данные едут. Никаких спиннеров и надписей «Загружаем…» — ТЗ 11, тишина. */

const LogoMark = ({ width = 143 }) => (
  <Logo
    width={width}
    height={Math.round((width * 46) / 143)}
    className="breath"
    style={{ color: "var(--tg-hint)" }}
  />
);

/* Заставка на весь экран: подключение и загрузка данных целого раздела */
export const Splash = () => (
  <div className="fixed inset-0 flex items-center justify-center" style={{ background: "var(--tg-secondary-bg)" }}>
    <LogoMark />
  </div>
);

/* Загрузка куска уже открытого экрана — на месте, не перекрывая остальное.
   `fill` растягивает на остаток экрана, чтобы логотип встал по центру,
   а не прижимался к тому, что нарисовано выше. */
export const Loader = ({ fill = false }) => (
  <div className={`flex items-center justify-center ${fill ? "min-h-[60vh]" : "py-14"}`}>
    <LogoMark width={110} />
  </div>
);

export const Empty = ({ text = "Пока пусто", icon: Icon = Ion.fileTray }) => (
  <div className="flex flex-col items-center justify-center gap-2 py-12 px-8 text-center">
    <Icon size={26} style={{ color: "var(--tg-hint)" }} />
    <Hint>{text}</Hint>
  </div>
);

export const ErrorState = ({ text, onRetry }) => (
  <Block className="text-center">
    <Ion.warning size={26} style={{ color: "var(--fin-red)" }} className="mx-auto mb-2" />
    <div className="text-[15px] font-semibold mb-1">Не получилось</div>
    <Hint>{text}</Hint>
    {onRetry ? (
      <div className="mt-3">
        <button
          className="rounded-lg px-4 py-2 text-[15px] font-semibold active:opacity-70"
          style={{ background: "var(--tg-button)", color: "var(--tg-button-text)" }}
          onClick={onRetry}
        >
          Повторить
        </button>
      </div>
    ) : null}
  </Block>
);

/* ── Горизонтальные чипы-фильтры ──
   У чипа бывает аватарка (`avatar`) — тогда слева кружок, а левое поле уже:
   иначе картинка висела бы в середине пилюли. Аватарки нет — чип чисто текстовый,
   букву-заглушку не подставляем: ряд из серых кружков читается тяжелее имён. */
export function Chips({ items, value, onChange, className = "" }) {
  return (
    <div className={`hscroll flex gap-2 px-4 py-2 ${className}`}>
      {items.map((it) => {
        const active = it.id === value;
        return (
          <button
            key={it.id}
            onClick={() => onChange(it.id)}
            className={`shrink-0 inline-flex items-center gap-1.5 rounded-full py-1.5 pe-3.5 text-[13px] font-semibold whitespace-nowrap transition-colors active:opacity-70 ${it.avatar ? "ps-1" : "ps-3.5"}`}
            style={{
              background: active ? "var(--tg-button)" : "var(--tg-section-bg)",
              color: active ? "var(--tg-button-text)" : "var(--tg-text)",
            }}
          >
            {it.avatar ? <Avatar src={it.avatar} name={it.label} size={20} onDark={active} /> : null}
            {it.label}
            {it.badge != null && it.badge !== 0 ? (
              <span className="num opacity-70">{it.badge}</span>
            ) : null}
          </button>
        );
      })}
    </div>
  );
}

/* ── Чипы этапов под шапкой карточки: тап переводит запись по воронке ──
   Ряд стоит на фоне страницы, как на карточке разработки: выбранный залит
   цветом своего этапа (тем же, что колонка канбана), остальные — карточной
   подложкой с цветным текстом. */
export function StageChips({ stages, value, onChange, colorOf }) {
  return (
    <div className="hscroll flex gap-2 px-4 pt-3">
      {stages.map((s, i) => {
        const active = s === value;
        const color = colorOf?.(s, i);
        return (
          <button
            key={s}
            type="button"
            onClick={() => { if (!active) onChange(s); }}
            className="shrink-0 rounded-full px-3.5 py-1.5 text-[13px] font-semibold whitespace-nowrap active:opacity-70"
            style={{
              background: active ? color || "var(--tg-button)" : "var(--tg-section-bg)",
              color: active ? "#fff" : color || "var(--tg-text)",
            }}
          >
            {s}
          </button>
        );
      })}
    </div>
  );
}

/* ── Поле суммы с разрядами: в поле «1 250 000», наружу уходит «1250000».
   type=number разряды показывать не умеет, поэтому поле текстовое с числовой
   клавиатурой, а группировку рисуем сами. Копейки набираются через запятую. ── */
const parseMoney = (raw) => {
  const [whole = "", frac] = String(raw ?? "")
    .replace(",", ".")
    .replace(/[^\d.]/g, "")
    .split(".");
  return { whole: whole.replace(/^0+(?=\d)/, ""), frac: frac == null ? null : frac.slice(0, 2) };
};

const showMoney = (value) => {
  const { whole, frac } = parseMoney(value);
  const head = whole ? new Intl.NumberFormat("ru-RU").format(Number(whole)) : "";
  return frac == null ? head : `${head},${frac}`;
};

const rawMoney = (typed) => {
  const { whole, frac } = parseMoney(typed);
  return frac == null ? whole : `${whole}.${frac}`;
};

export function MoneyInput({ placeholder, value, onChange }) {
  return (
    <ListInput
      type="text"
      inputMode="decimal"
      inputClassName="num"
      placeholder={placeholder}
      value={showMoney(value)}
      onChange={(e) => onChange({ target: { value: rawMoney(e.target.value) } })}
    />
  );
}

/* ── Вкладка-пилюля со счётчиком: алерты и задачи воронки ──
   Выбранная заливается своим цветом, остальные — секционной подложкой.
   `compact` для рядов из трёх вкладок: подписи иначе не помещаются в строку. */
export function TabButton({ active, onClick, color, label, count, compact = false }) {
  return (
    <button
      onClick={onClick}
      className={`flex-1 min-w-0 rounded-full font-semibold active:opacity-70 flex items-center justify-center ${
        compact ? "gap-1.5 py-2 text-[13px]" : "gap-2 py-2.5 text-[14px]"
      }`}
      style={{
        background: active ? color : "var(--tg-section-bg)",
        color: active ? "#fff" : "var(--tg-text)",
      }}
    >
      <span className="truncate">{label}</span>
      <span
        className="num shrink-0 rounded-full px-1.5 text-[11px] font-bold"
        style={{
          background: active ? "rgba(255,255,255,0.25)" : `color-mix(in srgb, ${color} 16%, transparent)`,
          color: active ? "#fff" : color,
        }}
      >
        {count}
      </span>
    </button>
  );
}

/* ── Строка «ключ ⋯ значение» ── */
export const Row = ({ label, children, onClick, className = "" }) => (
  <div
    className={`flex items-center justify-between gap-3 px-4 py-2.5 ${onClick ? "active:opacity-60" : ""} ${className}`}
    onClick={onClick}
    role={onClick ? "button" : undefined}
  >
    <span className="text-[14px] shrink-0" style={{ color: "var(--tg-hint)" }}>
      {label}
    </span>
    <span className="text-[15px] font-medium text-right min-w-0 truncate">{children}</span>
  </div>
);

export const Sep = () => (
  <div className="h-px ml-4" style={{ background: "var(--tg-separator)" }} />
);

export { rub };
